import React, { useState, useEffect } from 'react';
import { LevelData } from '../data/curriculum';
import { useTTS } from '../hooks/useTTS';
import { factToSpeechText } from '../data/audioTextGenerator';
import FactDisplay from '../components/FactDisplay';
import { motion } from 'framer-motion';
import TTSPlaybackIndicator from '../components/TTSPlaybackIndicator';
import NumberLine from '../components/NumberLine';

interface Step3SeriesListeningProps {
  level: LevelData;
  onComplete: () => void;
}

const Step3SeriesListening: React.FC<Step3SeriesListeningProps> = ({ level, onComplete }) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(-1);
  const { playText, isLoading, isPlaying, error: ttsError, stop } = useTTS();

  useEffect(() => {
    let cancelled = false;

    const playSequence = async () => {
      for (let i = 0; i < level.primaryFacts.length; i++) {
        if (cancelled) return;
        setCurrentFactIndex(i);
        await playText(factToSpeechText(level.primaryFacts[i]));
        if (cancelled) return;
        // Brief pause between facts
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
      if (!cancelled) {
        // Delay before advancing
        setTimeout(() => {
          if (!cancelled) onComplete();
        }, 1000);
      }
    };

    playSequence();

    return () => {
      cancelled = true;
      stop();
    };
  }, [level.primaryFacts, playText, onComplete, stop]);

  const rangeStart = Math.min(...level.primaryFacts.map((f) => f.addend1));
  const rangeEnd = Math.max(...level.primaryFacts.map((f) => f.sum)) + 1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-lg w-full"
    >
      {/* Ladder of facts */}
      <div className="flex flex-col items-center gap-3 mb-6">
        {level.primaryFacts.map((fact, idx) => {
          if (idx > currentFactIndex) return null;
          const isCurrent = idx === currentFactIndex;
          return (
            <motion.div
              key={`${fact.addend1}_${fact.addend2}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{
                opacity: 1,
                x: 0,
                scale: isCurrent ? 1.1 : 0.9,
              }}
              transition={{ duration: 0.3 }}
            >
              <FactDisplay fact={fact} showAnswer={true} />
            </motion.div>
          );
        })}
      </div>

      {/* Number line visualization */}
      {level.visualization === 'number-line' && currentFactIndex >= 0 && (
        <NumberLine
          key={currentFactIndex}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          fact={level.primaryFacts[currentFactIndex]}
          animate={true}
        />
      )}

      <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />
    </motion.div>
  );
};

export default Step3SeriesListening;
